import { useEffect, useState } from 'react';
import axios from 'axios';
import { apiClient } from '../../../utils/Api';
import store from '../../../store';
import { logout } from '../../../actions/authAction';

export const RELISTING_ACCOUNTS_SETTING = 'ka_relisting_accounts';
const CHANNELS = ['shopee', 'tiktok', 'ka'];

export const parseRelistingAccounts = setting => {
  if (!setting || setting.active !== true || setting.value_type !== 'json') {
    throw new Error('KA relisting accounts are not configured. Configure ka_relisting_accounts in Settings.');
  }
  let value = setting.value;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch (error) {
      throw new Error('KA relisting accounts contain invalid JSON. Update ka_relisting_accounts in Settings.');
    }
  }
  const isEmail = email => typeof email === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  if (!value || !Array.isArray(value.emails) || !value.emails.length || !value.emails.every(isEmail)) {
    throw new Error('KA relisting accounts must contain a non-empty emails list with valid email addresses. Update ka_relisting_accounts in Settings.');
  }
  const emails = Array.from(new Set(value.emails.map(email => email.trim())));
  const defaults = value.defaults;
  if (!defaults || typeof defaults !== 'object' || Array.isArray(defaults) || CHANNELS.some(channel => (
    !isEmail(defaults[channel]) || !emails.includes(defaults[channel].trim())
  ))) {
    throw new Error('KA relisting defaults for shopee, tiktok, and ka must refer to addresses in the emails list. Update ka_relisting_accounts in Settings.');
  }
  return {
    emails,
    defaults: { shopee: defaults.shopee.trim(), tiktok: defaults.tiktok.trim(), ka: defaults.ka.trim() }
  };
};

export const useFetchRelistingAccounts = () => {
  const [state, setState] = useState({ isLoading: true, accounts: null, error: '' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let mounted = true;
    const cancellation = axios.CancelToken.source();
    setState({ isLoading: true, accounts: null, error: '' });
    // Public settings support Sales roles without granting Settings administration.
    apiClient().get('/settings', {
      params: { name: RELISTING_ACCOUNTS_SETTING, per_page: 1 },
      cancelToken: cancellation.token,
      timeout: 15000
    }).then(response => {
      const page = response.data && response.data.data;
      if (!page || !Array.isArray(page.data)) throw new Error('Unable to read KA relisting accounts. Please try again.');
      const setting = page.data.find(item => item.name === RELISTING_ACCOUNTS_SETTING);
      const accounts = parseRelistingAccounts(setting);
      if (mounted) setState({ isLoading: false, accounts, error: '' });
    }).catch(error => {
      if (!mounted || axios.isCancel(error)) return;
      const status = error.response && error.response.status;
      let message = error.response
        ? 'Unable to load KA relisting accounts. Please try again.'
        : 'Unable to load KA relisting accounts. Check your connection and try again.';
      if (status === 401) {
        message = 'Your session has expired. Please sign in again.';
        store.dispatch(logout());
      } else if (status === 429) {
        message = 'Too many requests. Please wait before trying again.';
      } else if (status === 422) {
        message = 'Unable to load KA relisting accounts due to a validation error.';
      } else if (!error.response && !error.config && error instanceof Error) {
        message = error.message;
      }
      setState({ isLoading: false, accounts: null, error: message });
    });
    return () => {
      mounted = false;
      cancellation.cancel();
    };
  }, [attempt]);

  return { ...state, retry: () => setAttempt(previous => previous + 1) };
};
